import React, { useState } from "react";
import { IconButton, Typography, Box, Menu, MenuItem } from "@mui/material";
import { withRouter } from "react-router-dom";
import {
  Delete as DeleteIcon,
  CloudDownload as CloudDownloadIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { AlertDialog } from "../../../../../components/Dialog/AlertDialog";
import { RenameDialog } from "../../../../../components/Dialog/RenameDialog";
import { api } from "../../../../../Assets/api/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatDate } from "../utils/fileOperations";

const DetailSidebarHeader = ({ file, slug, refreshFiles, onDeleted }) => {
  if (!file) return null;

  // Validate slug before proceeding
  if (!slug || typeof slug !== "string" || slug.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          User information not available
        </Typography>
      </Box>
    );
  }

  const [loading, setLoading] = useState({
    isDeleting: false,
    isDeleteModalOpen: false,
    isDownloading: false,
    isRenaming: false,
    isRenameModalOpen: false,
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newFileName, setNewFileName] = useState("");
  const { t } = useTranslation();
  const translation = t("fileExplorer");

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget); // Open menu
  };

  const handleClose = () => {
    setAnchorEl(null); // Close menu
  };

  const handleFileRename = () => {
    setLoading((prev) => ({ ...prev, isRenameModalOpen: true }));
    handleClose();
  };

  const copyFilePath = () => {
    try {
      const filePath = file.path || file.blobName || file.name;
      navigator.clipboard.writeText(filePath);
      toast.success(translation.filePathCopied);
      handleClose();
    } catch (error) {
      toast.error(translation.failedToCopyPath);
    }
  };

  const renameFile = async (newName) => {
    try {
      setLoading((prev) => ({ ...prev, isRenaming: true }));

      const isFolder = file.isFolder || !file.name.includes(".");
      const oldBlobName = file.path || file.blobName || file.name;
      let newBlobName = newName;

      // If it's a nested file/folder, preserve the path structure
      if (oldBlobName.includes("/")) {
        const lastSlashIndex = oldBlobName.lastIndexOf("/");
        const directory = oldBlobName.substring(0, lastSlashIndex + 1);
        newBlobName = directory + newName;
      }

      const response = await api.post(`/api/account-storage/rename${slug}`, {
        containerName: slug.replace("?slug=", ""),
        oldBlobName: oldBlobName,
        newBlobName: newBlobName,
        slug: slug.replace("?slug=", ""),
      });

      if (response?.status === 200 || response?.data?.success === true) {
        toast.success(
          response?.data?.message || translation.fileRenamedSuccess
        );
        if (typeof refreshFiles === "function") refreshFiles();
      } else {
        toast.error(response?.data?.message || translation.failedToRenameFile);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || translation.errorRenamingFile
      );
    } finally {
      setLoading({
        ...loading,
        isRenaming: false,
        isRenameModalOpen: false,
      });
    }
  };

  const deleteFile = async (file) => {
    try {
      setLoading((prev) => ({ ...prev, isDeleting: true }));
      // Using the same endpoint but the backend now moves to recycle bin instead of permanent deletion
      const response = await api.delete(
        `/api/account-storage/${file.id}${slug}`
      );

      const success =
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data?.success === true ||
        response?.data?.sucess === "true";

      if (success) {
        toast.success(
          response?.data?.message || translation.fileMovedToRecycleBin
        );
        if (typeof refreshFiles === "function") refreshFiles();
        if (typeof onDeleted === "function") onDeleted();
      } else {
        toast.error(
          response?.data?.message || translation.failedToMoveToRecycleBin
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || translation.errorMovingToRecycleBin
      );
    } finally {
      setLoading({
        isDeleting: false,
        isDeleteModalOpen: false,
      });
    }
  };

  const downloadFile = async (file) => {
    try {
      setLoading((prev) => ({ ...prev, isDownloading: true }));
      const downloadId = file?.id || file?.blobName || file?.path || file?.name;
      const response = await api.get(
        `/api/account-storage/download/${encodeURIComponent(
          downloadId
        )}${slug}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data]);
      // Try to derive filename from headers; fallback to file.name
      const disposition = response?.headers?.["content-disposition"] || "";
      let filename = file?.name || "download";
      const match = disposition.match(
        /filename[^;=\n]*=((['"])?.*?\2|[^;\n]*)/i
      );
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "");
      }

      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error downloading file");
    } finally {
      setLoading((prev) => ({ ...prev, isDownloading: false }));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        height: "100%",
        px: 2,
        py: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          pr: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.2,
          }}
          title={file.name}
        >
          {file.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: "0.75rem",
            lineHeight: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {translation.edited} {formatDate(file.modified || file.lastModified)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          sx={{ color: "#666", padding: "6px" }}
          onClick={() => {
            setLoading((prev) => ({
              ...prev,
              isDeleteModalOpen: true,
            }));
          }}
          disabled={loading.isDeleting}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ color: "#666", padding: "6px" }}
          onClick={() => downloadFile(file)}
          disabled={loading.isDownloading}
        >
          <CloudDownloadIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleFileRename}>
            {file.isFolder || !file.name.includes(".")
              ? translation.renameFolder
              : translation.renameFile}
          </MenuItem>
          <MenuItem onClick={copyFilePath}>{translation.copyFilePath}</MenuItem>
        </Menu>
        <IconButton
          size="small"
          sx={{ color: "#666", padding: "6px" }}
          onClick={handleMenuClick}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <AlertDialog
        open={loading.isDeleteModalOpen}
        loading={loading.isDeleting}
        handleClose={() =>
          !loading.isDeleting &&
          setLoading((prev) => ({ ...prev, isDeleteModalOpen: false }))
        }
        handleOk={() => {
          if (!loading.isDeleting) deleteFile(file);
        }}
        warningMessage={translation.confirmMoveToRecycleBin}
        topMessage={translation.moveToRecycleBinTitle}
        undoMessage={translation.canRestoreFromRecycleBin}
      />
      <RenameDialog
        open={loading.isRenameModalOpen}
        loading={loading.isRenaming}
        handleClose={() =>
          !loading.isRenaming &&
          setLoading((prev) => ({ ...prev, isRenameModalOpen: false }))
        }
        handleRename={(newName) => {
          if (!loading.isRenaming) {
            renameFile(newName);
          }
        }}
        initialName={file.name}
        isFolder={file.isFolder || !file.name.includes(".")}
      />
    </Box>
  );
};

export default withRouter(DetailSidebarHeader);
